import {
	Box,
	Button,
	Flex,
	HStack,
	Icon,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	Tag,
	TagLabel,
	TagCloseButton,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import { FaCloudUploadAlt, FaEye, FaEyeSlash } from 'react-icons/fa';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useEffect, useRef, useState } from 'react';
import { StepFooter } from './StepFooter';

type Provider = 'mitchell' | 'opstrax';

const PROVIDER_LABELS: Record<Provider, string> = {
	mitchell: 'Mitchell',
	opstrax: 'OPS',
};

interface LocationCredentialsTableProps {
	locations: Array<{ _id: string; title: string }>;
	provider: Provider;
	onSave: (credentials: Array<{ locationId: string; username: string; password: string }>) => Promise<void>;
	onSkip: () => void;
	onBack?: () => void;
	isLoading?: boolean;
	initialUsernames?: Record<string, string>;
	isUploadOpen?: boolean;
	onCloseUpload?: () => void;
}

export const LocationCredentialsTable = ({
	locations,
	provider,
	onSave,
	onSkip,
	onBack,
	isLoading = false,
	initialUsernames = {},
	isUploadOpen,
	onCloseUpload,
}: LocationCredentialsTableProps) => {
	const toast = useToast();
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const [rows, setRows] = useState(
		locations.map((location) => ({
			locationId: location._id,
			locationName: location.title,
			username: initialUsernames[location._id] ?? '',
			password: '',
		}))
	);
	const [showPasswords, setShowPasswords] = useState(false);
	const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
	const preUploadRows = useRef<typeof rows | null>(null);
	const internalModal = useDisclosure();
	const isModalOpen = isUploadOpen ?? internalModal.isOpen;
	const closeModal = onCloseUpload ?? internalModal.onClose;
	const [isDragging, setIsDragging] = useState(false);
	const [templateProvider, setTemplateProvider] = useState<Provider>(provider);

	useEffect(() => {
		setRows((prev) =>
			locations.map((location) => {
				const existing = prev.find((row) => row.locationId === location._id);
				return {
					locationId: location._id,
					locationName: location.title,
					username: existing?.username ?? initialUsernames[location._id] ?? '',
					password: existing?.password ?? '',
				};
			})
		);
	}, [locations, initialUsernames]);

	const providerLabel = PROVIDER_LABELS[provider];

	const updateCell = (locationId: string, key: 'username' | 'password', value: string) => {
		setRows((prev) => prev.map((row) => (row.locationId === locationId ? { ...row, [key]: value } : row)));
	};

	const cellText = (value: ExcelJS.CellValue): string => {
		if (value == null) return '';
		if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
		if (typeof value === 'object') {
			if ('richText' in value) return value.richText.map((r: { text: string }) => r.text).join('');
			if ('text' in value) return String((value as { text: string }).text);
			if ('result' in value) return cellText((value as { result: ExcelJS.CellValue }).result);
		}
		return '';
	};

	const handleDownloadTemplate = async (downloadProvider?: Provider) => {
		const p = downloadProvider ?? provider;
		const label = p === 'mitchell' ? 'mitchell' : 'ops';
		const wb = new ExcelJS.Workbook();
		const ws = wb.addWorksheet('Credentials');

		const headerRow = ws.getRow(1);
		headerRow.values = ['Location Name', 'Username', 'Password'];
		headerRow.font = { bold: true };

		ws.getColumn(1).width = 35;
		ws.getColumn(2).width = 25;
		ws.getColumn(3).width = 25;

		rows.forEach((row, i) => {
			const r = ws.getRow(i + 2);
			r.getCell(1).value = row.locationName;
		});

		const buffer = await wb.xlsx.writeBuffer();
		saveAs(
			new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
			`${label}-location-credentials.xlsx`
		);
	};

	const handleUploadFile = async (file: File) => {
		try {
			const buffer = await file.arrayBuffer();
			const wb = new ExcelJS.Workbook();
			await wb.xlsx.load(buffer);

			const ws = wb.worksheets[0];
			if (!ws) {
				toast({ title: 'Empty file', description: 'The file contains no sheets.', status: 'error', duration: 3000, isClosable: true });
				return;
			}

			const normalizedLocationNameToId = new Map<string, string>(
				rows.map((row) => [row.locationName.trim().toLowerCase(), row.locationId])
			);

			const uploaded = new Map<string, { username: string; password: string }>();
			let unmatchedCount = 0;

			ws.eachRow((row, rowNumber) => {
				if (rowNumber === 1) return;
				const locationName = cellText(row.getCell(1).value).trim();
				if (!locationName) return;
				const locationId = normalizedLocationNameToId.get(locationName.toLowerCase());
				if (!locationId) {
					unmatchedCount++;
					return;
				}
				uploaded.set(locationId, {
					username: cellText(row.getCell(2).value).trim(),
					password: cellText(row.getCell(3).value).trim(),
				});
			});

			if (uploaded.size === 0) {
				toast({ title: 'No matches found', description: 'None of the location names matched your locations.', status: 'error', duration: 4000, isClosable: true });
				return;
			}

			let bothCount = 0;
			let usernameOnlyCount = 0;
			let passwordOnlyCount = 0;
			uploaded.forEach((entry) => {
				const hasUser = entry.username.length > 0;
				const hasPass = entry.password.length > 0;
				if (hasUser && hasPass) bothCount++;
				else if (hasUser) usernameOnlyCount++;
				else if (hasPass) passwordOnlyCount++;
			});

			preUploadRows.current = [...rows.map((r) => ({ ...r }))];

			setRows((prev) =>
				prev.map((row) => {
					const found = uploaded.get(row.locationId);
					if (!found) return row;
					return {
						...row,
						username: found.username || row.username,
						password: found.password || row.password,
					};
				})
			);

			setUploadedFileName(file.name);

			const parts: string[] = [];
			if (bothCount > 0) parts.push(`${bothCount} with username & password`);
			if (usernameOnlyCount > 0) parts.push(`${usernameOnlyCount} with username only`);
			if (passwordOnlyCount > 0) parts.push(`${passwordOnlyCount} with password only`);
			const detail = parts.join(', ');

			if (unmatchedCount > 0) {
				toast({ title: `${uploaded.size} of ${uploaded.size + unmatchedCount} location(s) matched`, description: `${detail}. ${unmatchedCount} row(s) did not match and were skipped.`, status: 'warning', duration: 5000, isClosable: true });
			} else {
				toast({ title: 'File uploaded successfully', description: `${uploaded.size} location(s) populated: ${detail}.`, status: 'success', duration: 4000, isClosable: true });
			}
		} catch {
			toast({ title: 'Failed to read file', description: 'The file could not be parsed.', status: 'error', duration: 4000, isClosable: true });
		}
	};

	const handleRemoveUpload = () => {
		if (preUploadRows.current) {
			setRows(preUploadRows.current);
			preUploadRows.current = null;
		}
		setUploadedFileName(null);
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const handleSave = async () => {
		const credentials = rows
			.filter((row) => row.username.trim().length > 0 || row.password.trim().length > 0)
			.map((row) => ({
				locationId: row.locationId,
				username: row.username.trim(),
				password: row.password.trim(),
			}));

		if (credentials.length === 0) {
			onSkip();
			return;
		}

		const invalid = credentials.find((row) => !row.username || !row.password);
		if (invalid) {
			toast({
				title: 'Invalid credentials',
				description: 'Username and password are required for each filled row.',
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
			return;
		}

		await onSave(credentials);
	};

	return (
		<Flex direction="column" flex={1} minH={0}>
			{/* Uploaded file indicator */}
			{uploadedFileName && (
				<HStack mb={2} flexShrink={0}>
					<Tag size="sm" colorScheme="green" borderRadius="full" variant="subtle">
						<TagLabel fontSize="xs">{uploadedFileName}</TagLabel>
						<TagCloseButton onClick={handleRemoveUpload} />
					</Tag>
				</HStack>
			)}

			{/* Hidden file input */}
			<input
				ref={fileInputRef}
				type="file"
				accept=".xlsx,.xls,.csv,.tsv"
				style={{ display: 'none' }}
				onChange={(e) => {
					const file = e.target.files?.[0];
					if (file) {
						void handleUploadFile(file);
						closeModal();
					}
					if (e.target) e.target.value = '';
				}}
			/>

			{/* Table with sticky header + scrollable body */}
			<Box borderWidth={1} borderRadius="md" overflow="hidden" display="flex" flexDirection="column" flex={1} minH={0}>
				<Table size="sm" sx={{ tableLayout: 'fixed' }}>
					<colgroup>
						<col style={{ width: '40%' }} />
						<col style={{ width: '30%' }} />
						<col style={{ width: '30%' }} />
					</colgroup>
					<Thead bg="gray.50">
						<Tr>
							<Th bg="gray.50" py={3.5}>Location</Th>
							<Th bg="gray.50" py={3.5}>{providerLabel} Username</Th>
							<Th bg="gray.50" py={3.5}>
								<HStack spacing={1}>
									<Text as="span">{providerLabel} Password</Text>
									<Flex onClick={() => setShowPasswords(!showPasswords)} cursor="pointer" ml={1}>
										<Icon as={(showPasswords ? FaEyeSlash : FaEye) as React.ElementType} color="gray.400" boxSize={3} />
									</Flex>
								</HStack>
							</Th>
						</Tr>
					</Thead>
				</Table>
				<Box flex={1} minH={0} overflowY="auto" overflowX="auto">
					<Table size="sm" sx={{ tableLayout: 'fixed' }}>
						<colgroup>
							<col style={{ width: '40%' }} />
							<col style={{ width: '30%' }} />
							<col style={{ width: '30%' }} />
						</colgroup>
						<Tbody>
							{rows.map((row) => (
								<Tr key={row.locationId}>
									<Td>{row.locationName}</Td>
									<Td>
										<Input
											size="sm"
											value={row.username}
											onChange={(e) => updateCell(row.locationId, 'username', e.target.value)}
											placeholder={`${providerLabel} username`}
										/>
									</Td>
									<Td>
										<Input
											size="sm"
											type={showPasswords ? 'text' : 'password'}
											value={row.password}
											onChange={(e) => updateCell(row.locationId, 'password', e.target.value)}
											placeholder={`${providerLabel} password`}
										/>
									</Td>
								</Tr>
							))}
						</Tbody>
					</Table>
				</Box>
			</Box>

			<StepFooter
				back={onBack ? { onClick: onBack } : undefined}
				secondaryActions={[{ label: 'Skip', onClick: onSkip }]}
				primary={{
					label: 'Save & Continue',
					onClick: () => void handleSave(),
					isLoading,
				}}
			/>

			{/* Upload Template Modal */}
			<Modal isOpen={isModalOpen} onClose={closeModal} size="lg" isCentered>
				<ModalOverlay />
				<ModalContent borderRadius="xl">
					<ModalHeader fontSize="lg" fontWeight="bold" pb={2}>Upload file</ModalHeader>
					<ModalCloseButton />
					<ModalBody pb={4}>
						{/* Drag & drop zone */}
						<Flex
							direction="column"
							align="center"
							justify="center"
							border="2px dashed"
							borderColor={isDragging ? 'teal.400' : 'teal.200'}
							borderRadius="xl"
							bg={isDragging ? 'teal.100' : '#EFF9FA'}
							py={10}
							px={4}
							cursor="pointer"
							onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
							onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
							onDragLeave={() => setIsDragging(false)}
							onDrop={(e) => {
								e.preventDefault();
								setIsDragging(false);
								const file = e.dataTransfer.files?.[0];
								if (file) {
									void handleUploadFile(file);
									closeModal();
								}
							}}
							onClick={() => fileInputRef.current?.click()}
							transition="all 0.15s"
						>
							<Flex
								w="48px" h="48px" borderRadius="full" bg="white"
								align="center" justify="center" mb={3}
								boxShadow="none"
							>
								<Icon as={FaCloudUploadAlt as React.ElementType} color="teal.400" boxSize={5} />
							</Flex>
							<Text fontSize="sm" color="gray.600">
								Drag and drop file here or{' '}
								<Text as="span" color="teal.500" textDecoration="underline" fontWeight="medium">
									Choose file
								</Text>
							</Text>
						</Flex>

						{/* Supported formats */}
						<Flex justify="space-between" mt={3} px={1}>
							<Text fontSize="xs" color="gray.500">Supported formats: PDF, CSV, TSV, XLS, XLSX</Text>
							<Text fontSize="xs" color="gray.500">Maximum size: 25 MB</Text>
						</Flex>

						{/* Provider selector */}
						<Flex
							bg="gray.100"
							borderRadius="full"
							p={1}
							mt={5}
							width="fit-content"
						>
							{(['mitchell', 'opstrax'] as Provider[]).map((p) => {
								const label = PROVIDER_LABELS[p];
								const isActive = templateProvider === p;
								return (
									<Box
										key={p}
										as="button"
										onClick={() => setTemplateProvider(p)}
										px={5}
										py={1.5}
										borderRadius="full"
										bg={isActive ? 'white' : 'transparent'}
										boxShadow={isActive ? 'sm' : 'none'}
										color={isActive ? 'gray.800' : 'gray.500'}
										fontWeight={isActive ? 'semibold' : 'medium'}
										fontSize="sm"
										cursor="pointer"
										transition="all 0.15s"
										_hover={{ color: isActive ? 'gray.800' : 'gray.700' }}
										border="none"
									>
										{label}
									</Box>
								);
							})}
						</Flex>

						{/* Template download card */}
						<Box
							borderWidth={1}
							borderColor="gray.200"
							borderRadius="lg"
							p={4}
							mt={3}
						>
							<Flex align="center" justify="space-between">
								<Flex align="center" gap={3}>
									<Flex
										w="40px" h="40px" borderRadius="md" bg="green.50"
										align="center" justify="center" flexShrink={0}
									>
										<Text fontSize="2xs" fontWeight="bold" color="green.500" letterSpacing="tight">XLSX</Text>
									</Flex>
									<Box>
										<Text fontSize="sm" fontWeight="semibold">Template File</Text>
										<Text fontSize="xs" color="gray.500">Download the template and use it as a starting point.</Text>
									</Box>
								</Flex>
								<Button
									size="sm"
									variant="outline"
									onClick={() => void handleDownloadTemplate(templateProvider)}
									flexShrink={0}
								>
									Download
								</Button>
							</Flex>
						</Box>
					</ModalBody>
					<ModalFooter borderTop="1px solid" borderColor="gray.300">
						<Button size="action" variant="outlineGray" onClick={closeModal}>Cancel</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Flex>
	);
};

